import { useEffect, useRef, useState, useSyncExternalStore } from "react"
import { cn } from "~/common/cn"
import { podTiers, type PodTierId } from "~/content/pods"
import { EffortSelector } from "~/marketing/pod-components"

const ADVANCE_MS = 4000
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"

const subscribeToReducedMotion = (onChange: () => void) => {
  const query = window.matchMedia(REDUCED_MOTION_QUERY)
  query.addEventListener("change", onChange)
  return () => query.removeEventListener("change", onChange)
}

const usePrefersReducedMotion = () =>
  useSyncExternalStore(
    subscribeToReducedMotion,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => true
  )

export const PodEffortPreview = ({
  initial,
  onChoose,
}: {
  initial: PodTierId
  onChoose: (tier: PodTierId) => void
}) => {
  const [shown, setShown] = useState<PodTierId>(initial)
  const [chosen, setChosen] = useState(false)
  const [paused, setPaused] = useState(false)
  const reducedMotion = usePrefersReducedMotion()
  const containerRef = useRef<HTMLDivElement>(null)

  const autoAdvancing = !reducedMotion && !chosen && !paused

  useEffect(() => {
    if (!autoAdvancing) return
    const timer = window.setTimeout(() => {
      setShown((current) => {
        const index = podTiers.findIndex((tier) => tier.id === current)
        return podTiers[(index + 1) % podTiers.length].id
      })
    }, ADVANCE_MS)
    return () => window.clearTimeout(timer)
  }, [autoAdvancing, shown])

  const choose = (tier: PodTierId) => {
    setChosen(true)
    setShown(tier)
    onChoose(tier)
  }

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!containerRef.current?.contains(event.relatedTarget as Node | null)) setPaused(false)
      }}
    >
      <h2 className="mb-4 text-2xl font-semibold tracking-tight">How fast do you want to move?</h2>
      <div className="w-fit max-w-full">
        <EffortSelector value={shown} onChange={choose} />
        <div className="mt-3 h-0.5 w-full overflow-hidden rounded-full bg-border">
          {autoAdvancing ? (
            <span
              key={shown}
              aria-hidden="true"
              className="pod-preview-progress block h-full bg-primary"
              style={{ animationDuration: `${ADVANCE_MS}ms` }}
            />
          ) : null}
        </div>
      </div>
      <div aria-live={chosen ? "polite" : "off"} className="mt-5 grid">
        {podTiers.map((tier) => (
          <TierPanel key={tier.id} tier={tier} visible={tier.id === shown} />
        ))}
      </div>
    </div>
  )
}

const TierPanel = ({ tier, visible }: { tier: (typeof podTiers)[number]; visible: boolean }) => (
  <div
    aria-hidden={!visible}
    className={cn(
      "flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm [grid-area:1/1]",
      visible ? "pod-preview-enter" : "invisible"
    )}
  >
    <div className="flex items-center justify-between gap-4">
      <p className="text-2xl font-semibold tracking-[-0.01em]">
        {tier.name} <span className="text-muted-foreground">effort</span>
      </p>
      <div aria-label={`${tier.effort} of 4 effort`} className="flex gap-1">
        {[1, 2, 3, 4].map((segment) => (
          <span
            key={segment}
            className={cn(
              "h-2 w-6 rounded-full bg-border",
              visible && "pod-preview-segment",
              segment <= tier.effort && "bg-primary"
            )}
            style={{ animationDelay: `${segment * 90}ms` }}
          />
        ))}
      </div>
    </div>
    <p className="mt-4 text-4xl font-semibold tracking-tight">
      {tier.price}
      {tier.id !== "max" ? (
        <span className="text-base font-normal text-muted-foreground"> /month</span>
      ) : null}
    </p>
    <h3 className="mt-4 text-2xl font-semibold leading-tight tracking-tight">{tier.outcome}</h3>
    <p className="mt-3 leading-relaxed text-muted-foreground">{tier.description}</p>
    <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{tier.cadence}</p>
    <div className="mt-auto pt-4">
      <p className="border-t border-border pt-4 text-sm leading-relaxed text-muted-foreground">
        <strong className="text-foreground">Best for:</strong> {tier.bestFor}
      </p>
    </div>
  </div>
)
